import { FunctionComponent } from 'react';
import { Flex, Icon, IconButton, Image, Menu, MenuButton, MenuItem, MenuList, Td, Text, useToken } from '@chakra-ui/react';
import { Reorder, useDragControls } from 'framer-motion';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { RosterGroup } from '@/model';
import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { DragHandle } from '@/pages/ApplicationChampionshipStageStandings/components/StandingsTable/DragHandle';

type RosterGroupRowProps = {
  item: RosterGroup;
  isMultiTeam: boolean;
  isDragDisabled?: boolean;
  onEditClick: (item: RosterGroup) => void;
  onDeleteClick: (item: RosterGroup) => void;
};

export const RosterGroupRow: FunctionComponent<RosterGroupRowProps> = ({
  item,
  isMultiTeam,
  isDragDisabled = false,
  onEditClick,
  onDeleteClick,
}) => {
  const { t } = useTranslation();
  const dragControls = useDragControls();
  const [borderColor] = useToken('colors', ['gray.100']);

  return (
    <Reorder.Item
      as='tr'
      value={item.uuid}
      dragListener={false}
      dragControls={dragControls}
      style={{
        background: 'white',
        borderTop: `1px solid ${borderColor}`,
        touchAction: 'none',
      }}
    >
      <Td px={2} w='32px'>
        <DragHandle
          isDisabled={isDragDisabled}
          onPointerDown={event => dragControls.start(event)}
          aria-label='drag'
        />
      </Td>

      {isMultiTeam ? (
        <Td px={3}>
          <Flex alignItems='center' gap={2}>
            {item.team_image ? (
              <Image src={item.team_image} alt={item.team_name || ''} w='24px' h='24px' objectFit='contain' />
            ) : null}
            <Text fontWeight='bold'>{item.team_name || ''}</Text>
          </Flex>
        </Td>
      ) : null}

      <Td px={3}>
        <Text>{item.name}</Text>
      </Td>

      <Td px={3} textAlign='center'>
        <Text>{item.sort_order}</Text>
      </Td>

      <Td px={3} textAlign='center'>
        <Text>
          {item.show_stats
            ? t('pages.application_roster_group.components.roster_group_table.roster_group_table_yes')
            : t('pages.application_roster_group.components.roster_group_table.roster_group_table_no')}
        </Text>
      </Td>

      <Td px={3}>
        <Text>{item.created_at}</Text>
      </Td>

      <Td px={2} textAlign='right'>
        <Flex justifyContent='end'>
          <Menu>
            <MenuButton
              textColor='text.table'
              as={IconButton}
              variant='ghost'
              size='sm'
              icon={<Icon as={EllipsisVerticalIcon} />}
              aria-label={t('pages.application_roster_group.components.roster_group_table.roster_group_table_show_more_actions')}
            />
            <MenuList textColor='white'>
              <MenuItem
                onClick={() => onEditClick(item)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='eye' />
                  </Text>
                }
              >
                <Text>{t('pages.application_roster_group.components.roster_group_table.roster_group_table_view_details')}</Text>
              </MenuItem>
              <MenuItem
                onClick={() => onDeleteClick(item)}
                icon={
                  <Text>
                    <ChakraFeatherIcon name='trash-2' color='red.500' w='18px' />
                  </Text>
                }
              >
                <Text>{t('pages.application_roster_group.components.roster_group_table.roster_group_table_delete')}</Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Td>
    </Reorder.Item>
  );
};
